import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import Select from '../ui/Select';
import StatusBanner from '../ui/StatusBanner';
import { apiRequest } from '../../services/apiClient';
import { API_BASE_URL } from '../../config/api';
import { buildDefaultTenantConfig, DEFAULT_TENANT_TERMINOLOGY, mergeTenantTerminology } from '../../config/tenantTerminology';
import {
  INDUSTRY_TEMPLATE_APPLY_MODE,
  INDUSTRY_TEMPLATE_APPLY_MODE_OPTIONS,
  INDUSTRY_TEMPLATE_STATUS,
  getIndustryTemplateStatusLabel
} from '../../config/industryTemplates';
import { getNavigationForRole } from '../../config/appRoutes';
import { useTenantConfig } from '../../context/TenantConfigContext';
import {
  buildEnabledModuleSet,
  buildResolvedModuleCatalog,
  disableModuleIfAllowed,
  enableModuleWithDependencies,
  filterNavigationByEnabledModules,
  MODULE_AVAILABILITY,
  resolveModuleIdForPath
} from '../../config/tenantModules';
import './TenantConfigurationPanel.css';

export const INDUSTRY_OPTIONS = [
  { value: 'general_operations', label: 'Operations' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'government', label: 'Government' },
  { value: 'other', label: 'Other' }
];

const ACCEPTED_LOGO_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export const TERMINOLOGY_SECTIONS = [
  {
    key: 'people',
    title: 'People',
    fields: [
      { key: 'employee', label: 'Employee' },
      { key: 'employees', label: 'Employees' },
      { key: 'manager', label: 'Manager' },
      { key: 'managers', label: 'Managers' }
    ]
  },
  {
    key: 'organization',
    title: 'Organization',
    fields: [
      { key: 'location', label: 'Location' },
      { key: 'locations', label: 'Locations' },
      { key: 'department', label: 'Department' },
      { key: 'departments', label: 'Departments' }
    ]
  },
  {
    key: 'workspace',
    title: 'Workspace',
    fields: [
      { key: 'workforce', label: 'Workforce' },
      { key: 'managerNotes', label: 'Manager Notes' },
      { key: 'goals', label: 'Goals' },
      { key: 'scorecards', label: 'Scorecards' },
      { key: 'requests', label: 'Requests' }
    ]
  }
];

function cloneConfiguration(configuration = {}) {
  return {
    ...buildDefaultTenantConfig({
      ...configuration,
      terminology: mergeTenantTerminology(configuration?.terminology || {})
    }),
    logoStorageKey: configuration?.logoStorageKey || null
  };
}

function createFallbackConfiguration(companyId, companyName) {
  return cloneConfiguration({
    companyId: companyId || null,
    companyDisplayName: companyName || '',
    terminology: DEFAULT_TENANT_TERMINOLOGY
  });
}

function normalizeFieldValue(value) {
  return String(value || '').trim();
}

function enabledModulesMatch(left, right) {
  return JSON.stringify(Array.from(buildEnabledModuleSet(left))) === JSON.stringify(Array.from(buildEnabledModuleSet(right)));
}

function configurationsMatch(left, right) {
  return JSON.stringify(cloneConfiguration(left)) === JSON.stringify(cloneConfiguration(right));
}

function getValueAtPath(configuration, path) {
  return path.split('.').reduce((current, segment) => current?.[segment], configuration);
}

function setValueAtPath(configuration, path, nextValue) {
  const [section, key] = path.split('.');
  return {
    ...configuration,
    terminology: {
      ...configuration.terminology,
      [section]: {
        ...configuration.terminology[section],
        [key]: nextValue
      }
    }
  };
}

export function validateConfiguration(configuration) {
  const errors = {};
  const companyDisplayName = normalizeFieldValue(configuration.companyDisplayName);
  if (!companyDisplayName) {
    errors.companyDisplayName = 'Company display name is required.';
  } else if (companyDisplayName.length > 200) {
    errors.companyDisplayName = 'Company display name must be 200 characters or fewer.';
  }

  TERMINOLOGY_SECTIONS.forEach((section) => {
    section.fields.forEach((field) => {
      const path = `${section.key}.${field.key}`;
      const value = normalizeFieldValue(getValueAtPath(configuration.terminology, path));
      if (!value) {
        errors[path] = `${field.label} cannot be blank.`;
      } else if (value.length > 80) {
        errors[path] = `${field.label} must be 80 characters or fewer.`;
      }
    });
  });

  return errors;
}

function buildRequestBody(configuration, originalConfiguration) {
  const body = {
    companyDisplayName: normalizeFieldValue(configuration.companyDisplayName),
    industryKey: normalizeFieldValue(configuration.industryKey) || 'general_operations',
    logoStorageKey: configuration.logoStorageKey || null,
    logoUrl: configuration.logoStorageKey ? null : (configuration.logoUrl || null),
    terminology: TERMINOLOGY_SECTIONS.reduce((accumulator, section) => {
      accumulator[section.key] = section.fields.reduce((sectionValues, field) => {
        sectionValues[field.key] = normalizeFieldValue(configuration.terminology?.[section.key]?.[field.key]);
        return sectionValues;
      }, {});
      return accumulator;
    }, {})
  };

  if (!originalConfiguration || !enabledModulesMatch(configuration.enabledModules, originalConfiguration.enabledModules)) {
    body.enabledModules = Array.from(buildEnabledModuleSet(configuration.enabledModules));
  }

  return body;
}

function normalizeResponseConfiguration(configuration, companyId, companyName) {
  return cloneConfiguration({
    ...createFallbackConfiguration(companyId, companyName),
    ...(configuration || {})
  });
}

function formatBytes(bytes) {
  if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.ceil(bytes / 1024)} KB`;
}

function validateLogoFile(file) {
  if (!file) return 'Logo file is required.';
  if (!ACCEPTED_LOGO_MIME_TYPES.includes(file.type)) {
    return 'Logo must be a JPEG, PNG, or WebP image.';
  }
  if (file.size > MAX_LOGO_BYTES) {
    return `Logo must be 2 MB or smaller. This file is ${formatBytes(file.size)}.`;
  }
  return '';
}

function buildUploadUrl(path) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

function summarizeTemplateTerminology(template) {
  if (!template?.terminology) return [];
  const terminology = mergeTenantTerminology(template.terminology);
  return [
    terminology.people.employee,
    terminology.people.manager,
    terminology.organization.location,
    terminology.workspace.workforce,
    terminology.workspace.requests
  ].filter(Boolean);
}

function summarizeTemplateModules(template) {
  return (template?.resolvedModules || [])
    .filter((module) => module.enabled)
    .map((module) => module.name);
}

function TenantConfigurationPreview({ configuration }) {
  const terminology = useMemo(
    () => mergeTenantTerminology(configuration?.terminology || {}),
    [configuration?.terminology]
  );
  const enabledModuleSet = useMemo(
    () => buildEnabledModuleSet(configuration?.enabledModules),
    [configuration?.enabledModules]
  );
  const navigation = useMemo(
    () => filterNavigationByEnabledModules(
      getNavigationForRole('director', terminology),
      enabledModuleSet,
      resolveModuleIdForPath
    ),
    [terminology, enabledModuleSet]
  );
  const navigationItems = navigation.flatMap((group) => group.items || []);
  const previewItems = [
    navigation.find((group) => group.label)?.label || terminology.workspace.workforce,
    navigationItems.find((item) => item.path === '/workforce')?.label || terminology.workspace.workforce,
    enabledModuleSet.has('manager_notes') ? terminology.workspace.managerNotes : null,
    enabledModuleSet.has('workforce') ? 'Team Directory' : null,
    navigationItems.find((item) => item.path === '/requests-calendar')?.label || (enabledModuleSet.has('requests') ? terminology.workspace.requests : null)
  ].filter(Boolean);

  return (
    <section className="tenant-config-card tenant-config-preview-card" aria-label="Live Preview">
      <div className="role-section-header">
        <h2>Live Preview</h2>
        <span>Updates immediately after save</span>
      </div>

      <div className="tenant-config-preview-brand">
        {configuration.logoUrl ? (
          <img
            src={configuration.logoUrl}
            alt={`${configuration.companyDisplayName || 'Tenant'} logo preview`}
            className="tenant-config-preview-logo"
          />
        ) : (
          <div className="tenant-config-preview-logo tenant-config-preview-logo--placeholder" aria-hidden="true">
            {normalizeFieldValue(configuration.companyDisplayName || 'Tenant').slice(0, 2).toUpperCase()}
          </div>
        )}
        <div>
          <strong>{configuration.companyDisplayName || 'Company Display Name'}</strong>
          <div className="director-muted">
            {INDUSTRY_OPTIONS.find((option) => option.value === configuration.industryKey)?.label || 'Operations'}
          </div>
        </div>
      </div>

      <div className="tenant-config-preview-grid">
        <div className="tenant-config-preview-panel">
          <h3>Navigation</h3>
          <ul>
            {previewItems.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
          </ul>
        </div>
        <div className="tenant-config-preview-panel">
          <h3>People</h3>
          <ul>
            <li>{terminology.people.employee}</li>
            <li>{terminology.people.manager}</li>
            <li>{terminology.organization.location}</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function ModuleStatusPill({ module }) {
  const statusLabel = module.status === MODULE_AVAILABILITY.COMING_SOON
    ? 'Coming Soon'
    : (module.status === MODULE_AVAILABILITY.DEPRECATED
      ? 'Deprecated'
      : (module.enabled ? 'Enabled' : 'Disabled'));
  const variant = module.status === MODULE_AVAILABILITY.COMING_SOON
    ? 'warning'
    : (module.status === MODULE_AVAILABILITY.DEPRECATED
      ? 'neutral'
      : (module.enabled ? 'success' : 'neutral'));

  return <StatusBanner kind={variant} message={statusLabel} className="tenant-config-module-pill" />;
}

function TemplateStatusPill({ status }) {
  const normalizedStatus = status || INDUSTRY_TEMPLATE_STATUS.ACTIVE;
  const kind = normalizedStatus === INDUSTRY_TEMPLATE_STATUS.DEPRECATED
    ? 'warning'
    : (normalizedStatus === INDUSTRY_TEMPLATE_STATUS.PREVIEW ? 'info' : 'success');
  return (
    <StatusBanner
      kind={kind}
      message={getIndustryTemplateStatusLabel(normalizedStatus)}
      className="tenant-config-module-pill"
    />
  );
}

function TerminologyField({
  sectionKey,
  fieldKey,
  label,
  value,
  defaultValue,
  error,
  disabled,
  onChange,
  onReset
}) {
  const inputId = `tenant-config-${sectionKey}-${fieldKey}`;

  return (
    <div className="tenant-config-term-row">
      <FormField
        label={label}
        htmlFor={inputId}
        error={error}
        hint={`Default: ${defaultValue}`}
        className="tenant-config-term-field"
      >
        <Input
          id={inputId}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      </FormField>
      <div className="tenant-config-term-meta">
        <span className="tenant-config-term-current">Current value: {value || defaultValue}</span>
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled || normalizeFieldValue(value) === normalizeFieldValue(defaultValue)}
          onClick={onReset}
        >
          Reset field
        </Button>
      </div>
    </div>
  );
}

function DirectorStatus({ isLoading }) {
  if (!isLoading) return null;
  return <StatusBanner kind="info" message="Loading tenant configuration..." className="role-inline-message" />;
}

function TenantConfigurationPanel({
  companyId,
  companyName,
  endpoint,
  canEdit = false,
  canManageIndustryTemplates = false,
  shouldRefreshProvider = false,
  onSaved
}) {
  const { refresh } = useTenantConfig();
  const requestIdRef = useRef(0);
  const templatesRequestIdRef = useRef(0);
  const previewRequestIdRef = useRef(0);
  const previousEndpointRef = useRef(endpoint);
  const fileInputRef = useRef(null);
  const fallbackConfiguration = useMemo(
    () => createFallbackConfiguration(companyId, companyName),
    [companyId, companyName]
  );
  const [originalConfiguration, setOriginalConfiguration] = useState(fallbackConfiguration);
  const [draftConfiguration, setDraftConfiguration] = useState(fallbackConfiguration);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [moduleSearch, setModuleSearch] = useState('');
  const [moduleSelectionTouched, setModuleSelectionTouched] = useState(false);
  const [status, setStatus] = useState({ kind: '', message: '' });
  const [industryTemplates, setIndustryTemplates] = useState([]);
  const [templatesError, setTemplatesError] = useState('');
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateMode, setTemplateMode] = useState(INDUSTRY_TEMPLATE_APPLY_MODE.FILL_MISSING_ONLY);
  const [applyTemplateTerminology, setApplyTemplateTerminology] = useState(true);
  const [applyTemplateModules, setApplyTemplateModules] = useState(true);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [templatePreviewError, setTemplatePreviewError] = useState('');
  const [isLoadingTemplatePreview, setIsLoadingTemplatePreview] = useState(false);
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);
  const originalRef = useRef(originalConfiguration);
  const draftRef = useRef(draftConfiguration);

  useEffect(() => {
    originalRef.current = originalConfiguration;
  }, [originalConfiguration]);

  useEffect(() => {
    draftRef.current = draftConfiguration;
  }, [draftConfiguration]);

  useEffect(() => {
    if (!companyId || !endpoint) {
      setOriginalConfiguration(fallbackConfiguration);
      setDraftConfiguration(fallbackConfiguration);
      setModuleSelectionTouched(false);
      setErrors({});
      setStatus({ kind: '', message: '' });
      previousEndpointRef.current = endpoint;
      return;
    }

    const switchedTenant = Boolean(previousEndpointRef.current && previousEndpointRef.current !== endpoint);
    const hadUnsavedChanges = switchedTenant && !configurationsMatch(originalRef.current, draftRef.current);
    previousEndpointRef.current = endpoint;

    if (switchedTenant) {
      const blank = createFallbackConfiguration(companyId, companyName);
      setOriginalConfiguration(blank);
      setDraftConfiguration(blank);
      setModuleSelectionTouched(false);
      setErrors({});
      setStatus(hadUnsavedChanges
        ? { kind: 'info', message: 'Discarded unsaved changes and loaded the newly selected tenant.' }
        : { kind: '', message: '' });
    } else {
      setStatus({ kind: '', message: '' });
    }

    const loadId = ++requestIdRef.current;
    setIsLoading(true);

    apiRequest(endpoint)
      .then((payload) => {
        if (loadId !== requestIdRef.current) return;
        const resolved = normalizeResponseConfiguration(payload?.configuration, companyId, companyName);
        setOriginalConfiguration(resolved);
        setDraftConfiguration(resolved);
        setModuleSelectionTouched(false);
        setErrors({});
      })
      .catch((error) => {
        if (loadId !== requestIdRef.current) return;
        const fallback = createFallbackConfiguration(companyId, companyName);
        setOriginalConfiguration(fallback);
        setDraftConfiguration(fallback);
        setModuleSelectionTouched(false);
        setStatus({
          kind: 'error',
          message: error?.message || 'Unable to load tenant configuration.'
        });
      })
      .finally(() => {
        if (loadId === requestIdRef.current) {
          setIsLoading(false);
        }
      });
  }, [companyId, companyName, endpoint, fallbackConfiguration]);

  useEffect(() => {
    if (!canManageIndustryTemplates || !companyId) {
      previewRequestIdRef.current += 1;
      setIndustryTemplates([]);
      setTemplatesError('');
      setSelectedTemplateId('');
      setTemplateMode(INDUSTRY_TEMPLATE_APPLY_MODE.FILL_MISSING_ONLY);
      setApplyTemplateTerminology(true);
      setApplyTemplateModules(true);
      setTemplatePreview(null);
      setTemplatePreviewError('');
      return;
    }

    setSelectedTemplateId('');
    setTemplateMode(INDUSTRY_TEMPLATE_APPLY_MODE.FILL_MISSING_ONLY);
    setApplyTemplateTerminology(true);
    setApplyTemplateModules(true);
    previewRequestIdRef.current += 1;
    setTemplatePreview(null);
    setTemplatePreviewError('');

    const loadId = ++templatesRequestIdRef.current;
    setIsLoadingTemplates(true);
    setTemplatesError('');

    apiRequest('/api/platform/industry-templates')
      .then((payload) => {
        if (loadId !== templatesRequestIdRef.current) return;
        setIndustryTemplates(Array.isArray(payload?.templates) ? payload.templates : []);
      })
      .catch((error) => {
        if (loadId !== templatesRequestIdRef.current) return;
        setIndustryTemplates([]);
        setTemplatesError(error?.message || 'Unable to load industry templates.');
      })
      .finally(() => {
        if (loadId === templatesRequestIdRef.current) {
          setIsLoadingTemplates(false);
        }
      });
  }, [canManageIndustryTemplates, companyId]);

  const hasUnsavedChanges = useMemo(
    () => moduleSelectionTouched || !configurationsMatch(originalConfiguration, draftConfiguration),
    [draftConfiguration, moduleSelectionTouched, originalConfiguration]
  );
  const selectedTemplate = useMemo(
    () => industryTemplates.find((template) => template.id === selectedTemplateId) || null,
    [industryTemplates, selectedTemplateId]
  );
  const templateOptions = useMemo(() => industryTemplates.map((template) => ({
    value: template.id,
    label: `${template.name}${template.status === INDUSTRY_TEMPLATE_STATUS.DEPRECATED ? ' (Deprecated)' : ''}`
  })), [industryTemplates]);
  const resolvedModules = useMemo(
    () => buildResolvedModuleCatalog(draftConfiguration.enabledModules),
    [draftConfiguration.enabledModules]
  );
  const normalizedModuleSearch = normalizeFieldValue(moduleSearch).toLowerCase();
  const moduleGroups = useMemo(() => resolvedModules.reduce((accumulator, module) => {
    if (normalizedModuleSearch) {
      const matches = [
        module.name,
        module.description,
        module.category
      ].some((value) => String(value || '').toLowerCase().includes(normalizedModuleSearch));
      if (!matches) {
        return accumulator;
      }
    }
    if (!accumulator[module.category]) {
      accumulator[module.category] = [];
    }
    accumulator[module.category].push(module);
    return accumulator;
  }, {}), [normalizedModuleSearch, resolvedModules]);

  const updateDraftValue = (path, nextValue) => {
    setDraftConfiguration((current) => setValueAtPath(current, path, nextValue));
    setErrors((current) => {
      const nextErrors = { ...current };
      delete nextErrors[path];
      return nextErrors;
    });
    setStatus({ kind: '', message: '' });
  };

  const handleBrandFieldChange = (key, nextValue) => {
    setDraftConfiguration((current) => ({
      ...current,
      [key]: nextValue
    }));
    setErrors((current) => {
      const nextErrors = { ...current };
      delete nextErrors[key];
      return nextErrors;
    });
    setStatus({ kind: '', message: '' });
  };

  const handleCancel = () => {
    setDraftConfiguration(originalConfiguration);
    setModuleSelectionTouched(false);
    setErrors({});
    setStatus({ kind: '', message: '' });
  };

  const resetTemplateSelection = () => {
    setSelectedTemplateId('');
    setTemplateMode(INDUSTRY_TEMPLATE_APPLY_MODE.FILL_MISSING_ONLY);
    setApplyTemplateTerminology(true);
    setApplyTemplateModules(true);
    setTemplatePreview(null);
    setTemplatePreviewError('');
  };

  const resetTerminologySection = (sectionKey) => {
    setDraftConfiguration((current) => ({
      ...current,
      terminology: {
        ...current.terminology,
        [sectionKey]: { ...DEFAULT_TENANT_TERMINOLOGY[sectionKey] }
      }
    }));
    setErrors((current) => Object.fromEntries(
      Object.entries(current).filter(([key]) => !key.startsWith(`${sectionKey}.`))
    ));
    setStatus({ kind: '', message: '' });
  };

  const resetAllTerminology = () => {
    setDraftConfiguration((current) => ({
      ...current,
      terminology: mergeTenantTerminology({})
    }));
    setErrors((current) => Object.fromEntries(
      Object.entries(current).filter(([key]) => !key.includes('.'))
    ));
    setStatus({ kind: '', message: '' });
  };

  const resetModulesToDefaults = () => {
    const fallback = createFallbackConfiguration(companyId, companyName);
    setModuleSelectionTouched(true);
    setDraftConfiguration((current) => ({
      ...current,
      enabledModules: fallback.enabledModules
    }));
    setStatus({ kind: '', message: '' });
  };

  const handleModuleToggle = (module) => {
    if (!module || !canEdit || isLoading) return;
    if (module.status === MODULE_AVAILABILITY.COMING_SOON) {
      setStatus({ kind: 'info', message: `${module.name} is coming soon and cannot be enabled yet.` });
      return;
    }
    if (module.status === MODULE_AVAILABILITY.DEPRECATED) {
      setStatus({ kind: 'info', message: `${module.name} is deprecated and cannot be enabled.` });
      return;
    }

    const enabledSet = buildEnabledModuleSet(draftConfiguration.enabledModules);
    if (module.enabled) {
      const result = disableModuleIfAllowed(enabledSet, module.id);
      if (!result.changed) {
        setStatus({ kind: 'info', message: result.reason || `${module.name} cannot be disabled.` });
        return;
      }
      setDraftConfiguration((current) => ({
        ...current,
        enabledModules: Array.from(result.enabledSet)
      }));
      setModuleSelectionTouched(true);
      setStatus({ kind: '', message: '' });
      return;
    }

    const nextEnabledSet = enableModuleWithDependencies(enabledSet, module.id);
    const nextDependencies = (module.dependencies || [])
      .map((dependencyId) => resolvedModules.find((candidate) => candidate.id === dependencyId)?.name || dependencyId);
    setDraftConfiguration((current) => ({
      ...current,
      enabledModules: Array.from(nextEnabledSet)
    }));
    setModuleSelectionTouched(true);
    setStatus(nextDependencies.length
      ? { kind: 'info', message: `Enabled ${module.name} and its dependency ${nextDependencies.join(', ')} in the local draft.` }
      : { kind: '', message: '' });
  };

  const handleFileSelection = async (event) => {
    const [file] = Array.from(event.target.files || []);
    if (!file) return;

    const validationMessage = validateLogoFile(file);
    if (validationMessage) {
      setStatus({ kind: 'error', message: validationMessage });
      return;
    }

    const uploadEndpoint = `${endpoint}/logo`;
    setIsUploadingLogo(true);
    setStatus({ kind: '', message: '' });

    try {
      const body = new FormData();
      body.append('logo', file);
      const response = await fetch(buildUploadUrl(uploadEndpoint), {
        method: 'POST',
        credentials: 'include',
        body
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.success === false) {
        throw new Error(payload.error || `Logo upload failed (${response.status}).`);
      }
      const data = payload.data || {};
      setDraftConfiguration((current) => ({
        ...current,
        logoUrl: data.logoUrl || current.logoUrl || null,
        logoStorageKey: data.logoStorageKey || null
      }));
      setStatus({ kind: 'success', message: 'Logo uploaded. Save changes to apply it to this tenant.' });
    } catch (error) {
      setStatus({
        kind: 'error',
        message: error?.message || 'Unable to upload logo.'
      });
    } finally {
      setIsUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSave = async () => {
    const validationErrors = validateConfiguration(draftConfiguration);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setStatus({
        kind: 'error',
        message: 'Please fix the highlighted fields before saving.'
      });
      return;
    }

    setIsSaving(true);
    setStatus({ kind: '', message: '' });
    try {
      const payload = buildRequestBody(
        draftConfiguration,
        moduleSelectionTouched ? null : originalConfiguration
      );
      const response = await apiRequest(endpoint, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      const resolved = normalizeResponseConfiguration(response?.configuration, companyId, companyName);
      setOriginalConfiguration(resolved);
      setDraftConfiguration(resolved);
      setModuleSelectionTouched(false);
      setErrors({});
      setStatus({
        kind: 'success',
        message: 'Tenant configuration saved.'
      });
      if (shouldRefreshProvider) {
        await refresh();
      }
      onSaved?.(resolved);
    } catch (error) {
      setStatus({
        kind: 'error',
        message: error?.message || 'Unable to save tenant configuration.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const buildTemplatePreviewEndpoint = () => `/api/platform/companies/${companyId}/settings/industry-template/preview`;
  const buildTemplateApplyEndpoint = () => `/api/platform/companies/${companyId}/settings/industry-template/apply`;

  const handlePreviewTemplate = async () => {
    if (!selectedTemplate || !companyId) return;
    setIsLoadingTemplatePreview(true);
    setTemplatePreviewError('');
    const previewId = ++previewRequestIdRef.current;

    try {
      const response = await apiRequest(buildTemplatePreviewEndpoint(), {
        method: 'POST',
        body: JSON.stringify({
          templateId: selectedTemplate.id,
          templateVersion: selectedTemplate.version,
          applyMode: templateMode,
          applyTerminology: applyTemplateTerminology,
          applyModules: applyTemplateModules
        })
      });
      if (previewId !== previewRequestIdRef.current) return;
      setTemplatePreview(response?.preview || null);
    } catch (error) {
      if (previewId !== previewRequestIdRef.current) return;
      setTemplatePreview(null);
      setTemplatePreviewError(error?.message || 'Unable to preview this industry template.');
    } finally {
      if (previewId === previewRequestIdRef.current) {
        setIsLoadingTemplatePreview(false);
      }
    }
  };

  const handleApplyTemplate = async () => {
    if (!templatePreview || !selectedTemplate || !companyId) return;

    setIsApplyingTemplate(true);
    setTemplatePreviewError('');
    try {
      const response = await apiRequest(buildTemplateApplyEndpoint(), {
        method: 'POST',
        body: JSON.stringify({
          templateId: selectedTemplate.id,
          templateVersion: selectedTemplate.version,
          applyMode: templateMode,
          applyTerminology: applyTemplateTerminology,
          applyModules: applyTemplateModules,
          previewedCompanyId: companyId,
          previewedConfigurationUpdatedAt: templatePreview.currentConfigurationUpdatedAt
        })
      });

      const resolved = normalizeResponseConfiguration(response?.configuration, companyId, companyName);
      setOriginalConfiguration(resolved);
      setDraftConfiguration(resolved);
      setModuleSelectionTouched(false);
      setErrors({});
      setTemplatePreview(null);
      setStatus({
        kind: 'success',
        message: 'Industry template applied.'
      });
      if (shouldRefreshProvider) {
        await refresh();
      }
      onSaved?.(resolved);
    } catch (error) {
      setTemplatePreviewError(error?.message || 'Unable to apply this industry template.');
    } finally {
      setIsApplyingTemplate(false);
    }
  };

  if (!companyId || !endpoint) {
    return (
      <section className="director-card admin-section-intro">
        <div className="role-section-header">
          <h2>Tenant Configuration</h2>
          <span>Select a company</span>
        </div>
        <p className="role-section-copy">Choose a tenant to manage branding and terminology.</p>
      </section>
    );
  }

  return (
    <div className="tenant-config-layout" data-testid="tenant-configuration-panel">
      <section className="tenant-config-card">
        <div className="role-section-header">
          <h2>Branding</h2>
          <span>Customer-facing identity</span>
        </div>

        {status.message ? (
          <StatusBanner kind={status.kind || 'info'} message={status.message} className="role-inline-message" />
        ) : null}
        {!canEdit ? (
          <StatusBanner
            kind="info"
            message="You can review tenant branding and terminology here, but your current role does not allow edits."
            className="role-inline-message"
          />
        ) : null}
        <DirectorStatus isLoading={isLoading} />

        <div className="tenant-config-form-grid">
          <FormField
            label="Company Display Name"
            htmlFor="tenant-config-company-display-name"
            required
            error={errors.companyDisplayName}
            hint="This name appears in tenant-aware navigation and preview surfaces."
          >
            <Input
              id="tenant-config-company-display-name"
              value={draftConfiguration.companyDisplayName}
              disabled={!canEdit || isLoading}
              onChange={(event) => handleBrandFieldChange('companyDisplayName', event.target.value)}
            />
          </FormField>

          <FormField
            label="Industry"
            htmlFor="tenant-config-industry"
            hint="Sets the tenant’s industry classification for future templates and defaults."
          >
            <Select
              id="tenant-config-industry"
              value={draftConfiguration.industryKey}
              disabled={!canEdit || isLoading}
              options={INDUSTRY_OPTIONS}
              onChange={(event) => handleBrandFieldChange('industryKey', event.target.value)}
            />
          </FormField>
        </div>

        <div className="tenant-config-logo-row">
          <div className="tenant-config-logo-controls">
            <FormField
              label="Logo"
              htmlFor="tenant-config-logo-upload"
              hint="Upload a JPEG, PNG, or WebP logo up to 2 MB. Uploaded assets are stored through the shared tenant-safe storage layer."
            >
              <input
                ref={fileInputRef}
                id="tenant-config-logo-upload"
                type="file"
                accept={ACCEPTED_LOGO_MIME_TYPES.join(',')}
                className="tenant-config-file-input"
                disabled={!canEdit || isLoading || isUploadingLogo}
                onChange={handleFileSelection}
              />
            </FormField>
            <div className="director-table-actions">
              <Button
                size="sm"
                variant="secondary"
                disabled={!canEdit || isLoading || isUploadingLogo}
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploadingLogo ? 'Uploading...' : 'Upload Logo'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={!canEdit || isLoading || isUploadingLogo || (!draftConfiguration.logoUrl && !draftConfiguration.logoStorageKey)}
                onClick={() => {
                  setDraftConfiguration((current) => ({
                    ...current,
                    logoUrl: null,
                    logoStorageKey: null
                  }));
                  setStatus({ kind: '', message: '' });
                }}
              >
                Remove Logo
              </Button>
            </div>
          </div>

          <div className="tenant-config-preview-brand tenant-config-brand-preview-card">
            {draftConfiguration.logoUrl ? (
              <img
                src={draftConfiguration.logoUrl}
                alt={`${draftConfiguration.companyDisplayName || companyName || 'Tenant'} logo`}
                className="tenant-config-preview-logo"
              />
            ) : (
              <div className="tenant-config-preview-logo tenant-config-preview-logo--placeholder" aria-hidden="true">
                {normalizeFieldValue(draftConfiguration.companyDisplayName || companyName || 'Tenant').slice(0, 2).toUpperCase()}
              </div>
            )}
            <div>
              <strong>{draftConfiguration.companyDisplayName || companyName || 'Company Display Name'}</strong>
              <div className="director-muted">
                {INDUSTRY_OPTIONS.find((option) => option.value === draftConfiguration.industryKey)?.label || 'Operations'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {canManageIndustryTemplates ? (
        <section className="tenant-config-card" data-testid="tenant-config-industry-templates">
          <div className="role-section-header">
            <h2>Industry Template</h2>
            <span>Platform onboarding accelerator</span>
          </div>
          <p className="role-section-copy">
            Preview a recommended starting configuration before anything is saved. Templates never auto-apply and they do not overwrite branding assets, users, permissions, or operational records without explicit confirmation.
          </p>

          {templatesError ? (
            <StatusBanner kind="error" message={templatesError} className="role-inline-message" />
          ) : null}
          {templatePreviewError ? (
            <StatusBanner kind="error" message={templatePreviewError} className="role-inline-message" />
          ) : null}
          {hasUnsavedChanges ? (
            <StatusBanner
              kind="info"
              message="Save or cancel the current tenant draft before previewing or applying an industry template."
              className="role-inline-message"
            />
          ) : null}

          <div className="tenant-config-template-grid">
            <div className="tenant-config-template-controls">
              <FormField
                label="Template"
                htmlFor="tenant-config-industry-template"
                hint="Platform administrators can preview and apply templates only for the currently selected tenant."
              >
                <Select
                  id="tenant-config-industry-template"
                  value={selectedTemplateId}
                  disabled={isLoadingTemplates || isLoading || hasUnsavedChanges}
                  options={[{ value: '', label: isLoadingTemplates ? 'Loading templates...' : 'Select a template' }, ...templateOptions]}
                  onChange={(event) => {
                    setSelectedTemplateId(event.target.value);
                    setTemplatePreview(null);
                    setTemplatePreviewError('');
                  }}
                />
              </FormField>

              <FormField
                label="Apply Mode"
                htmlFor="tenant-config-industry-template-mode"
                hint="Choose whether the template fills only missing values or replaces terminology and modules explicitly."
              >
                <Select
                  id="tenant-config-industry-template-mode"
                  value={templateMode}
                  disabled={!selectedTemplate || isLoading || hasUnsavedChanges}
                  options={INDUSTRY_TEMPLATE_APPLY_MODE_OPTIONS}
                  onChange={(event) => {
                    setTemplateMode(event.target.value);
                    setTemplatePreview(null);
                    setTemplatePreviewError('');
                  }}
                />
              </FormField>

              <div className="tenant-config-template-checkboxes">
                <label className="tenant-config-template-checkbox">
                  <input
                    type="checkbox"
                    checked={applyTemplateTerminology}
                    disabled={!selectedTemplate || isLoading || hasUnsavedChanges}
                    onChange={(event) => {
                      setApplyTemplateTerminology(event.target.checked);
                      setTemplatePreview(null);
                      setTemplatePreviewError('');
                    }}
                  />
                  <span>Apply terminology</span>
                </label>
                <label className="tenant-config-template-checkbox">
                  <input
                    type="checkbox"
                    checked={applyTemplateModules}
                    disabled={!selectedTemplate || isLoading || hasUnsavedChanges}
                    onChange={(event) => {
                      setApplyTemplateModules(event.target.checked);
                      setTemplatePreview(null);
                      setTemplatePreviewError('');
                    }}
                  />
                  <span>Apply modules</span>
                </label>
              </div>

              <div className="director-table-actions">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!selectedTemplate || isLoading || hasUnsavedChanges || (!applyTemplateTerminology && !applyTemplateModules)}
                  onClick={handlePreviewTemplate}
                >
                  {isLoadingTemplatePreview ? 'Previewing...' : 'Preview Template'}
                </Button>
                <Button
                  size="sm"
                  disabled={!templatePreview || isLoading || isApplyingTemplate || hasUnsavedChanges}
                  onClick={handleApplyTemplate}
                >
                  {isApplyingTemplate ? 'Applying...' : 'Apply Template'}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={isLoading || isApplyingTemplate}
                  onClick={resetTemplateSelection}
                >
                  Clear Selection
                </Button>
              </div>
            </div>

            <div className="tenant-config-template-summary">
              <div className="tenant-config-template-summary-card">
                <div className="tenant-config-template-summary-header">
                  <div>
                    <strong>Current Industry</strong>
                    <div className="director-muted">
                      {INDUSTRY_OPTIONS.find((option) => option.value === originalConfiguration.industryKey)?.label || 'Operations'}
                    </div>
                  </div>
                  {selectedTemplate ? <TemplateStatusPill status={selectedTemplate.status} /> : null}
                </div>

                {selectedTemplate ? (
                  <div className="tenant-config-template-details">
                    <div>
                      <strong>{selectedTemplate.name}</strong>
                      <div className="director-muted">{selectedTemplate.description}</div>
                    </div>
                    <div className="tenant-config-template-meta">
                      <span>Version {selectedTemplate.version}</span>
                      <span>Updated {selectedTemplate.lastUpdated}</span>
                    </div>
                    <div className="tenant-config-template-list-block">
                      <strong>Recommended terminology</strong>
                      <div className="tenant-config-template-chip-list">
                        {summarizeTemplateTerminology(selectedTemplate).map((label) => (
                          <span key={label} className="tenant-config-template-chip">{label}</span>
                        ))}
                      </div>
                    </div>
                    <div className="tenant-config-template-list-block">
                      <strong>Recommended modules</strong>
                      <div className="tenant-config-template-chip-list">
                        {summarizeTemplateModules(selectedTemplate).map((label) => (
                          <span key={label} className="tenant-config-template-chip">{label}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="director-muted">Select a template to review its scope before previewing changes.</div>
                )}
              </div>
            </div>
          </div>

          {templatePreview ? (
            <div className="tenant-config-template-preview-grid">
              <section className="tenant-config-template-preview-card">
                <div className="role-section-header">
                  <h3>Industry</h3>
                  <span>{templatePreview.diff.industry.changed ? 'Will change' : 'Unchanged'}</span>
                </div>
                <div className="tenant-config-template-compare-row">
                  <span>Current</span>
                  <strong>{templatePreview.diff.industry.currentValue}</strong>
                </div>
                <div className="tenant-config-template-compare-row">
                  <span>Template</span>
                  <strong>{templatePreview.diff.industry.templateValue}</strong>
                </div>
                <div className="tenant-config-template-compare-row">
                  <span>Result</span>
                  <strong>{templatePreview.diff.industry.proposedValue}</strong>
                </div>
              </section>

              <section className="tenant-config-template-preview-card">
                <div className="role-section-header">
                  <h3>Terminology</h3>
                  <span>{templatePreview.diff.terminology.applyTerminology ? `${templatePreview.diff.terminology.changedFields.length} fields` : 'Skipped'}</span>
                </div>
                {templatePreview.diff.terminology.applyTerminology
                  ? (
                    templatePreview.diff.terminology.changedFields.length ? (
                      <div className="tenant-config-template-diff-list">
                        {templatePreview.diff.terminology.changedFields.map((field) => (
                          <div key={field.path} className="tenant-config-template-diff-row">
                            <strong>{field.path}</strong>
                            <span>{field.currentValue}</span>
                            <span>{field.proposedValue}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="director-muted">No terminology values will change in this mode.</div>
                    )
                  )
                  : <div className="director-muted">Terminology will remain unchanged.</div>}
              </section>

              <section className="tenant-config-template-preview-card">
                <div className="role-section-header">
                  <h3>Modules</h3>
                  <span>{templatePreview.diff.modules.applyModules ? `${templatePreview.diff.modules.proposed.length} enabled` : 'Skipped'}</span>
                </div>
                {templatePreview.diff.modules.applyModules ? (
                  <>
                    <div className="tenant-config-template-list-block">
                      <strong>Enable</strong>
                      <div className="tenant-config-template-chip-list">
                        {(templatePreview.diff.modules.toEnable.length
                          ? templatePreview.diff.modules.toEnable
                          : ['No additional modules']).map((moduleId) => (
                          <span key={`enable-${moduleId}`} className="tenant-config-template-chip">{moduleId}</span>
                        ))}
                      </div>
                    </div>
                    <div className="tenant-config-template-list-block">
                      <strong>Disable</strong>
                      <div className="tenant-config-template-chip-list">
                        {(templatePreview.diff.modules.toDisable.length
                          ? templatePreview.diff.modules.toDisable
                          : ['No modules removed']).map((moduleId) => (
                          <span key={`disable-${moduleId}`} className="tenant-config-template-chip">{moduleId}</span>
                        ))}
                      </div>
                    </div>
                    <div className="tenant-config-template-list-block">
                      <strong>Dependencies added automatically</strong>
                      <div className="tenant-config-template-chip-list">
                        {(templatePreview.diff.modules.dependenciesAddedAutomatically.length
                          ? templatePreview.diff.modules.dependenciesAddedAutomatically
                          : ['No added dependencies']).map((moduleId) => (
                          <span key={`dependency-${moduleId}`} className="tenant-config-template-chip">{moduleId}</span>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="director-muted">Module enablement will remain unchanged.</div>
                )}
              </section>
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="tenant-config-card">
        <div className="role-section-header">
          <h2>Terminology</h2>
          <span>Live label customization</span>
        </div>
        <p className="role-section-copy">
          Customize tenant-facing labels while keeping platform defaults visible for targeted resets.
        </p>
        <div className="director-table-actions tenant-config-terminology-actions">
          <Button
            size="sm"
            variant="secondary"
            disabled={!canEdit || isLoading}
            onClick={resetAllTerminology}
          >
            Reset All Terminology
          </Button>
          <span className="director-muted">Resets only the local draft until you save.</span>
        </div>

        <div className="tenant-config-terminology-grid">
          {TERMINOLOGY_SECTIONS.map((section) => (
            <section key={section.key} className="tenant-config-term-card">
              <div className="role-section-header">
                <h3>{section.title}</h3>
                <span>{section.fields.length} labels</span>
              </div>
              <div className="tenant-config-term-section-actions">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!canEdit || isLoading}
                  onClick={() => resetTerminologySection(section.key)}
                >
                  Reset Section
                </Button>
              </div>
              <div className="tenant-config-term-list">
                {section.fields.map((field) => {
                  const path = `${section.key}.${field.key}`;
                  const value = draftConfiguration.terminology?.[section.key]?.[field.key] || '';
                  const defaultValue = DEFAULT_TENANT_TERMINOLOGY[section.key][field.key];
                  return (
                    <TerminologyField
                      key={path}
                      sectionKey={section.key}
                      fieldKey={field.key}
                      label={field.label}
                      value={value}
                      defaultValue={defaultValue}
                      error={errors[path]}
                      disabled={!canEdit || isLoading}
                      onChange={(nextValue) => updateDraftValue(path, nextValue)}
                      onReset={() => updateDraftValue(path, defaultValue)}
                    />
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </section>

      <section className="tenant-config-card" data-testid="tenant-config-modules">
        <div className="role-section-header">
          <h2>Enabled Modules</h2>
          <span>Tenant licensing and workspace visibility</span>
        </div>
        <p className="role-section-copy">
          Enable only the modules this tenant licenses or actively uses. Navigation, routes, and
          supported workspace widgets update after save and provider refresh.
        </p>
        <div className="tenant-config-modules-toolbar">
          <FormField
            label="Search Modules"
            htmlFor="tenant-config-module-search"
            hint="Filter by module name, description, or category."
            className="tenant-config-modules-search"
          >
            <Input
              id="tenant-config-module-search"
              type="search"
              value={moduleSearch}
              disabled={isLoading}
              onChange={(event) => setModuleSearch(event.target.value)}
            />
          </FormField>
          <div className="tenant-config-modules-actions">
            <Button
              size="sm"
              variant="secondary"
              disabled={!canEdit || isLoading}
              onClick={resetModulesToDefaults}
            >
              Reset Modules
            </Button>
          </div>
        </div>

        <div className="tenant-config-modules-grid">
          {Object.entries(moduleGroups).map(([category, modules]) => (
            <section key={category} className="tenant-config-module-category">
              <div className="role-section-header">
                <h3>{category}</h3>
                <span>{modules.length} modules</span>
              </div>
              <div className="tenant-config-module-list">
                {modules.map((module) => {
                  const dependencyNames = (module.dependencies || []).map((dependencyId) => (
                    resolvedModules.find((candidate) => candidate.id === dependencyId)?.name || dependencyId
                  ));
                  const blockingDependentNames = (module.blockingDependents || []).map((dependentId) => (
                    resolvedModules.find((candidate) => candidate.id === dependentId)?.name || dependentId
                  ));
                  const toggleDisabled = !canEdit
                    || isLoading
                    || module.locked
                    || module.status === MODULE_AVAILABILITY.COMING_SOON
                    || module.status === MODULE_AVAILABILITY.DEPRECATED;

                  return (
                    <article key={module.id} className="tenant-config-module-row">
                      <div className="tenant-config-module-copy">
                        <div className="tenant-config-module-heading">
                          <div>
                            <strong>{module.name}</strong>
                            <div className="director-muted">{module.description}</div>
                          </div>
                          <ModuleStatusPill module={module} />
                        </div>
                        <div className="tenant-config-module-meta">
                          <span>Minimum role: {module.minimumRole}</span>
                          {dependencyNames.length ? (
                            <span>Requires: {dependencyNames.join(', ')}</span>
                          ) : (
                            <span>No dependencies</span>
                          )}
                        </div>
                        {module.locked ? (
                          <div className="director-muted">Always enabled for tenant safety.</div>
                        ) : null}
                        {blockingDependentNames.length ? (
                          <div className="director-muted">
                            Disable {blockingDependentNames.join(', ')} before turning this off.
                          </div>
                        ) : null}
                      </div>
                      <label className="tenant-config-module-toggle">
                        <input
                          type="checkbox"
                          checked={module.enabled}
                          disabled={toggleDisabled}
                          onChange={() => handleModuleToggle(module)}
                        />
                        <span>{module.enabled ? 'Enabled' : 'Disabled'}</span>
                      </label>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
          {!Object.keys(moduleGroups).length ? (
            <div className="director-muted">No modules match the current search.</div>
          ) : null}
        </div>
      </section>

      <TenantConfigurationPreview configuration={draftConfiguration} />

      {hasUnsavedChanges ? (
        <div className="tenant-config-save-bar" data-testid="tenant-config-save-bar">
          <div>
            <strong>Unsaved changes</strong>
            <div className="director-muted">Cancel restores the last persisted tenant configuration. Resets change only the local draft until save succeeds.</div>
          </div>
          <div className="tenant-config-save-actions">
            <Button size="sm" variant="secondary" disabled={isSaving || isUploadingLogo} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={isSaving}
              loadingLabel="Saving..."
              disabled={!canEdit || isLoading || isUploadingLogo}
              onClick={handleSave}
            >
              Save Changes
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default TenantConfigurationPanel;
